<main id="conteudo" class="listings-shell" tabindex="-1">
  @if (busy()) {
  <div class="loading-overlay" role="status" aria-live="polite">
    <div class="spinner"></div>
    <p>{{ syncing() ? 'Analisando anúncios ativos...' : 'Carregando diagnóstico dos anúncios...' }}</p>
    @if (syncProgress(); as progress) {
    <small>{{ progress.processed }} de {{ progress.total || '?' }} anúncios processados</small>
    }
  </div>
  }

  <header class="card listings-hero">
    <div>
      <span class="eyebrow">Qualidade dos anúncios</span>
      <h1>Dashboard de Anúncios</h1>
      <p>{{ reviewCountLabel() }} · títulos, descrições, fotos, ficha técnica e oportunidades de conversão.</p>
    </div>
    <div class="hero-actions no-print">
      <a class="btn btn-secondary" routerLink="/dashboard">Conciliação</a>
      <a class="btn btn-secondary" routerLink="/relatorios">Relatórios</a>
      <a class="btn btn-secondary" routerLink="/publicidade">Publicidade</a>
      <a class="btn btn-secondary" routerLink="/reputacao">Reputação</a>
      <button class="btn btn-secondary" type="button" (click)="logout()">Sair</button>
    </div>
  </header>

  <section class="card intro-card">
    <div>
      <span class="eyebrow">Para que serve</span>
      <h2>Fila de melhoria para vender melhor</h2>
      <p>O Mercado Livre é confuso mesmo. Aqui transformamos cada anúncio ativo em uma revisão prática: o que está errado, o que pode atrapalhar busca/conversão e qual deve ser o próximo ajuste.</p>
    </div>
    <div class="intro-actions no-print">
      <button class="btn btn-primary" type="button" [disabled]="busy()" (click)="syncActiveListings()">Atualizar anúncios ativos</button>
      <a class="btn btn-secondary" [href]="exportUrl">Exportar CSV para IA</a>
    </div>
  </section>

  <section class="card best-practices-card">
    <div>
      <span class="eyebrow">Padrão que vamos cobrar</span>
      <h2>Como um anúncio bom deve aparecer</h2>
    </div>
    <div class="practice-grid">
      <span>Título limpo, sem símbolos e sem gritaria.</span>
      <span>Compatibilidade clara usando “para”.</span>
      <span>Descrição separada por produto, aplicação, código, estado e observações.</span>
      <span>Nada de “OR1G1NAL”, “S-€-M-1-N-0-V-0” ou texto grudado.</span>
    </div>
  </section>

  @if (message()) { <section class="card info-card">{{ message() }}</section> }
  @if (error()) { <section class="card info-card error">{{ error() }}</section> }

  @if (totals(); as totals) {
  <section class="kpi-grid">
    <article class="card kpi"><span>Anúncios ativos lidos</span><strong>{{ totals.total }}</strong></article>
    <article class="card kpi warning"><span>Precisam revisar</span><strong>{{ totals.withIssues }}</strong></article>
    <article class="card kpi danger"><span>Texto estranho ou mal escrito</span><strong>{{ totals.badCharacters }}</strong></article>
    <article class="card kpi danger"><span>Descrição vazia</span><strong>{{ totals.emptyDescription }}</strong></article>
    <article class="card kpi warning"><span>Descrição curta</span><strong>{{ totals.shortDescription }}</strong></article>
    <article class="card kpi"><span>Nota média</span><strong>{{ totals.avgQualityScore | number : '1.0-0' }}/100</strong></article>
  </section>
  }

  <form class="card filters-card no-print" (ngSubmit)="applySearch()" aria-label="Filtros dos anúncios">
    <div class="field">
      <label for="listings-search">Busca</label>
      <input id="listings-search" name="search" type="search" placeholder="Título ou ID do anúncio" autocomplete="off" [value]="search()" (input)="setSearch($any($event.target).value)" />
    </div>
    <div class="field">
      <label for="listings-issue-filter">Problema</label>
      <select id="listings-issue-filter" name="issueFilter" [value]="issueFilter()" (change)="setIssueFilter($any($event.target).value)">
        <option value="all">Todos os problemas</option>
        <option value="bad_chars">Texto estranho ou mal escrito</option>
        <option value="description_empty">Descrição vazia</option>
        <option value="description_short">Descrição curta</option>
        <option value="few_pictures">Poucas fotos</option>
        <option value="few_attributes">Ficha técnica incompleta</option>
      </select>
    </div>
    <label class="toggle-row" for="listings-only-problems">
      <input id="listings-only-problems" name="onlyProblems" type="checkbox" [checked]="onlyProblems()" (change)="setOnlyProblems($any($event.target).checked)" />
      <span>Mostrar só anúncios com pontos de melhoria</span>
    </label>
    <button class="btn btn-secondary" type="submit" [disabled]="busy()">Aplicar filtros</button>
  </form>

  <section class="listings-grid">
    @for (listing of listings(); track listing.itemId) {
    <article class="card listing-card" [class.critical]="listing.qualityScore < 45">
      <div class="listing-media">
        @if (listing.thumbnail) {
        <img [src]="listing.thumbnail" [alt]="listing.title || listing.itemId" loading="lazy" decoding="async" />
        } @else {
        <div class="image-placeholder">Sem foto</div>
        }
      </div>
      <div class="listing-body">
        <div class="listing-topline">
          <span class="score" [ngClass]="qualityClass(listing.qualityScore)">{{ qualityLabel(listing.qualityScore) }} · {{ listing.qualityScore }}/100</span>
          <span class="item-id">{{ listing.itemId }}</span>
        </div>
        <h2>{{ listing.title || 'Anúncio sem título' }}</h2>
        <div class="meta-row">
          <span>{{ listing.price | currency : 'BRL' : 'symbol' : '1.2-2' }}</span>
          <span>{{ listing.picturesCount }} foto(s)</span>
          <span>{{ listing.attributesCount }} atributo(s)</span>
          <span>{{ listing.soldQuantity }} venda(s)</span>
        </div>

        <div class="primary-action">
          <strong>{{ primaryAction(listing) }}</strong>
          <p>{{ descriptionPreview(listing) }}</p>
        </div>

        @if (listing.issues.length > 0) {
        <div class="issue-list">
          @for (issue of listing.issues; track issue.code) {
          <span class="issue-chip" [ngClass]="issueClass(issue)" [title]="issue.detail">{{ issue.label }}</span>
          }
        </div>
        } @else {
        <div class="issue-list"><span class="issue-chip ok">Sem problema automático</span></div>
        }

        <div class="card-actions no-print">
          @if (listing.permalink) {
          <a class="btn btn-secondary" [href]="listing.permalink" target="_blank" rel="noopener noreferrer">Abrir no ML</a>
          }
          <button class="btn btn-primary" type="button" (click)="openRevision(listing)">Revisar e alterar</button>
          <button class="btn btn-primary" type="button" (click)="copyAiPrompt(listing)">
            {{ copiedItemId() === listing.itemId ? 'Prompt copiado' : 'Copiar prompt para IA' }}
          </button>
        </div>
      </div>
    </article>
    } @empty {
    <section class="card empty-state">
      <h2>Nenhum anúncio encontrado para estes filtros.</h2>
      <p>Se ainda não atualizamos os anúncios, clique em “Atualizar anúncios ativos”.</p>
    </section>
    }
  </section>

  @if (revisionDraft(); as draft) {
  <div class="revision-backdrop no-print" role="presentation" (click)="closeRevision()">
    <section class="revision-panel" role="dialog" aria-modal="true" aria-labelledby="revision-title" (click)="$event.stopPropagation()">
      <div class="revision-header">
        <div>
          <span class="eyebrow">Revisão manual</span>
          <h2 id="revision-title">Alterar título e descrição</h2>
          <p>{{ draft.listing.itemId }} · confira o antes e o depois antes de salvar no Mercado Livre.</p>
        </div>
        <button class="icon-button" type="button" [disabled]="draft.saving" aria-label="Fechar revisão" (click)="closeRevision()">x</button>
      </div>

      <div class="revision-grid">
        <section class="revision-column">
          <h3>Antes</h3>
          <label>
            Título atual
            <input type="text" [value]="draft.currentTitle || 'Sem título'" readonly />
          </label>
          <label>
            Descrição atual
            <textarea rows="12" [value]="draft.currentDescription || 'Sem descrição cadastrada.'" readonly></textarea>
          </label>
          <div class="revision-score">
            <span class="score" [ngClass]="qualityClass(draft.listing.qualityScore)">{{ draft.listing.qualityScore }}/100</span>
            <strong>{{ primaryAction(draft.listing) }}</strong>
          </div>
        </section>

        <form class="revision-column revision-form" (submit)="applyRevision(); $event.preventDefault()">
          <h3>Depois</h3>
          <label for="revision-new-title">
            Novo título
            <input
              id="revision-new-title"
              name="revisionTitle"
              type="text"
              [required]="!draft.titleLocked"
              minlength="10"
              maxlength="60"
              autocomplete="off"
              [value]="draft.newTitle"
              [readonly]="draft.titleLocked"
              [attr.aria-invalid]="revisionTitleCount() > 60"
              aria-describedby="revision-title-hint"
              (input)="setRevisionTitle($any($event.target).value)"
            />
          </label>
          <small id="revision-title-hint" class="field-hint" [class.warning]="revisionTitleCount() > 60 || draft.titleLocked">
            @if (draft.titleLocked) {
            Título bloqueado pelo Mercado Livre porque o anúncio usa family_name. Aplique só a descrição.
            } @else {
            {{ revisionTitleCount() }}/60 caracteres
            }
          </small>

          <label for="revision-new-description">
            Nova descrição
            <textarea
              id="revision-new-description"
              name="revisionDescription"
              rows="12"
              required
              [value]="draft.newDescription"
              (input)="setRevisionDescription($any($event.target).value)"
            ></textarea>
          </label>

          <div class="revision-tips">
            <strong>O que mudar/melhorar</strong>
            <ul>
              @for (tip of improvementTips(draft.listing); track tip) {
              <li>{{ tip }}</li>
              }
            </ul>
          </div>

          <p class="revision-warning">Antes de aplicar, confira compatibilidade, código da peça, motor/ano e estado. O Worker vai recusar se o anúncio tiver mudado desde que esta janela abriu.</p>
          @if (draft.error) { <p class="form-error" role="alert">{{ draft.error }}</p> }

          <div class="revision-actions">
            <button class="btn btn-secondary" type="button" [disabled]="draft.saving" (click)="closeRevision()">Cancelar</button>
            <button class="btn btn-primary" type="submit" [disabled]="!revisionCanSave()">
              {{ draft.saving ? 'Aplicando...' : 'Aplicar neste anúncio' }}
            </button>
          </div>
        </form>
      </div>
    </section>
  </div>
  }
</main>
